import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import api from "@/lib/api";
import EntityLogo from "@/components/EntityLogo";
import ImageUploadField from "@/components/ImageUploadField";
import { normalizeImageUrl } from "@/lib/imageUrl";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import TeamRosterTab from "@/components/team/TeamRosterTab";
import TeamStatsTab from "@/components/team/TeamStatsTab";
import TeamMediaTab from "@/components/team/TeamMediaTab";
import {
  Shield, MapPin, Users, Calendar, ChevronRight, TrendingUp, Info, Plus, X,
  Heart, BarChart3, Target, Award, FileText, BadgeCheck, Users2, UserPlus, Trash2, Copy, Crown,
  MoreVertical, Eye, Play, Archive as ArchiveIcon, RotateCcw, Trophy, Globe, Camera,
  Flame, Minus, TrendingDown, ClipboardCheck,
} from "lucide-react";

const rk = (v) => (v == null ? "—" : `#${v}`);

const TABS = [
  ["overview", "Overview"], ["roster", "Roster"], ["schedule", "Schedule"],
  ["results", "Results"], ["stats", "Stats"], ["media", "Media"],
];

function useTeamSeo(team) {
  useEffect(() => {
    if (!team) return;
    const title = `${team.name}${team.age_division ? ` · ${team.age_division}` : ""} — CBR Prospects`;
    const desc = `${team.name}${team.organization?.name ? ` (${team.organization.name})` : ""}${team.province ? `, ${team.province}` : ""}. CBR Team Rating, roster, schedule, results and stats on CBR Prospects.`;
    const img = team.primary_photo_url || team.effective_logo_url || team.logo_url || "";
    document.title = title;
    const set = (sel, attr, val) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement("meta"); const [a, v] = sel.replace(/meta\[|\]/g, "").split("="); el.setAttribute(a, v.replace(/["']/g, "")); document.head.appendChild(el); }
      el.setAttribute(attr, val);
    };
    set('meta[name="description"]', "content", desc);
    set('meta[property="og:title"]', "content", title);
    set('meta[property="og:description"]', "content", desc);
    if (img) set('meta[property="og:image"]', "content", normalizeImageUrl(img));
    set('meta[property="og:type"]', "content", "profile");
  }, [team]);
}

export default function LiveTeam() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [team, setTeam] = useState(null);
  const [sched, setSched] = useState({ upcoming: [], live: [], past: [], archived: [] });
  const [results, setResults] = useState({ results: [], summary: null });
  const [showForm, setShowForm] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const tablistRef = useRef(null);

  const rawTab = params.get("tab");
  const tab = TABS.some(([k]) => k === rawTab) ? rawTab : "overview";
  const setTab = (t) => { const p = new URLSearchParams(params); p.set("tab", t); setParams(p, { replace: true }); };

  const load = () => {
    api.get(`/live/teams/${slug}`).then(({ data }) => {
      setTeam(data);
      setFollowing(!!data.is_following);
      api.get(`/live/teams/${data.id}/schedule`).then((r) => setSched(r.data)).catch(() => {});
      api.get(`/live/teams/${data.id}/results`).then((r) => setResults(r.data)).catch(() => {});
    }).catch(() => setTeam(false));
  };
  useEffect(() => { load(); window.scrollTo(0, 0); }, [slug]);
  useTeamSeo(team && team !== false ? team : null);

  const toggleFollow = () => {
    if (!user) { toast.error("Sign in to follow teams"); return; }
    if (followBusy) return;
    const next = !following;
    setFollowing(next); setFollowBusy(true);
    const req = next ? api.post(`/live/teams/${team.id}/follow`) : api.delete(`/live/teams/${team.id}/follow`);
    req.then(() => toast.success(next ? "Following team" : "Unfollowed"))
      .catch(() => { setFollowing(!next); toast.error("Failed"); })
      .finally(() => setFollowBusy(false));
  };

  const onTabKey = (e) => {
    const i = TABS.findIndex(([k]) => k === tab);
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const ni = e.key === "ArrowRight" ? (i + 1) % TABS.length : (i - 1 + TABS.length) % TABS.length;
      setTab(TABS[ni][0]);
      requestAnimationFrame(() => tablistRef.current?.querySelector(`[data-tabkey="${TABS[ni][0]}"]`)?.focus());
    }
  };

  if (team === false) return <Shell><Empty>Team not found.</Empty></Shell>;
  if (!team) return <Shell><Empty>Loading team…</Empty></Shell>;

  const canManage = team.can_manage || (user && (user.role === "admin" || user.role === "super_admin" || user.id === team.created_by));
  const caps = team.capabilities || [];
  const canInviteStaff = caps.includes("invite_team_members");
  const rt = team.rating || {};
  const upcoming = [...(sched.live || []), ...(sched.upcoming || [])];

  return (
    <Shell>
      <TeamHero team={team} rt={rt} results={results} following={following} followBusy={followBusy} toggleFollow={toggleFollow} canManage={canManage} onPhoto={load} />

      {/* Sticky tab bar */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div ref={tablistRef} role="tablist" aria-label="Team sections" onKeyDown={onTabKey}
            className="flex gap-1 overflow-x-auto no-scrollbar">
            {TABS.map(([k, label]) => {
              const active = tab === k;
              return (
                <button key={k} role="tab" data-tabkey={k} data-testid={`tab-${k}`} id={`tab-${k}`}
                  aria-selected={active} aria-controls={`panel-${k}`} tabIndex={active ? 0 : -1}
                  onClick={() => setTab(k)}
                  className={`px-4 py-3.5 text-sm font-semibold uppercase tracking-wider whitespace-nowrap border-b-2 -mb-px transition-colors focus-visible:ring-2 focus-visible:ring-[#E63946] focus-visible:outline-none ${active ? "border-[#E63946] text-[#0A192F]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div key={tab} role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`} className="animate-fadein">
          {tab === "overview" && (
            <OverviewTab team={team} rt={rt} upcoming={upcoming} results={results} canInviteStaff={canInviteStaff} setTab={setTab} />
          )}
          {tab === "roster" && <TeamRosterTab roster={team.roster} />}
          {tab === "schedule" && (
            <ScheduleTab team={team} sched={sched} upcoming={upcoming} canManage={canManage} showForm={showForm} setShowForm={setShowForm} onChange={load} />
          )}
          {tab === "results" && <ResultsTab results={results} />}
          {tab === "stats" && <TeamStatsTab stats={team.stats} record={team.record} gamesPlayed={rt.games_played || 0} />}
          {tab === "media" && <TeamMediaTab team={team} />}
        </div>
      </div>
    </Shell>
  );
}

/* ------------------------------------------------------------------ Hero */
function TeamHero({ team, rt, results, following, followBusy, toggleFollow, canManage, onPhoto }) {
  const [editPhoto, setEditPhoto] = useState(false);
  const sum = results?.summary;
  const record = (sum && sum.games > 0 ? sum.record?.overall : null) || team.record?.overall || { w: 0, l: 0, t: 0 };
  const photo = team.primary_photo_url;
  const savePhoto = (url) => {
    api.patch(`/live/teams/${team.id}`, { primary_photo_url: url || null })
      .then(() => { toast.success("Team photo updated"); setEditPhoto(false); onPhoto(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
  };
  const socialLinks = Object.entries(team.social || {}).filter(([, v]) => v);
  const contact = team.contact || {};

  return (
    <div className="relative bg-[#0A192F] text-white overflow-hidden">
      {photo && (
        <>
          <img src={normalizeImageUrl(photo)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/85 to-[#0A192F]/60" />
        </>
      )}
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row items-start gap-5">
          <EntityLogo src={team.effective_logo_url || team.logo_url || team.organization?.logo_url} alt={team.name} size="w-20 h-20 sm:w-24 sm:h-24" textSize="text-2xl" variant="dark" />
          <div className="min-w-0 flex-1">
            <div className="label-overline text-[#E63946]">{[team.organization?.name || "CBR Live", team.age_division].filter(Boolean).join(" · ")}</div>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              <h1 data-testid="team-name" className="font-display text-4xl sm:text-5xl uppercase tracking-tighter leading-none">{team.name}</h1>
              {team.verified && <span data-testid="team-verified-badge" className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 px-2.5 py-1 rounded-full"><BadgeCheck className="w-3.5 h-3.5" /> Verified</span>}
              {team.founding_member && <span data-testid="team-founding-badge" className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider bg-[#E63946]/20 text-[#E63946] px-2.5 py-1 rounded-full"><Crown className="w-3.5 h-3.5" /> Founding Member</span>}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/70 mt-2">
              {team.league && <span>{team.league}</span>}
              {team.province && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{team.city_region ? `${team.city_region}, ` : ""}{team.province}</span>}
              {team.home_diamond && <span className="flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> {team.home_diamond}</span>}
            </div>

            {/* Stat strip */}
            <div className="flex flex-wrap items-stretch gap-2 mt-4">
              <HeroStat label="Record" value={`${record.w}-${record.l}${record.t ? `-${record.t}` : ""}`} testid="hero-record" />
              <HeroStat label="CBR Rating" value={rt.cbr_team_rating != null ? Number(rt.cbr_team_rating).toFixed(2) : "—"} testid="hero-rating" />
              <HeroStat label="National" value={rk(rt.national_rank)} testid="hero-national-rank" />
              <HeroStat label="Provincial" value={rk(rt.provincial_rank)} testid="hero-provincial-rank" />
              {rt.age_division_rank != null && <HeroStat label="Age Div" value={rk(rt.age_division_rank)} testid="hero-agediv-rank" />}
            </div>

            {/* Links */}
            {(team.website || socialLinks.length > 0 || contact.public_email || contact.public_phone) && (
              <div className="flex flex-wrap items-center gap-3 mt-4 text-white/70">
                {team.website && <a href={/^https?:\/\//i.test(team.website) ? team.website : `https://${team.website}`} target="_blank" rel="noreferrer" data-testid="hero-website" className="inline-flex items-center gap-1 text-xs hover:text-white"><Globe className="w-3.5 h-3.5" /> Website</a>}
                {socialLinks.map(([k, v]) => <a key={k} href={/^https?:\/\//i.test(v) ? v : `https://${v}`} target="_blank" rel="noreferrer" data-testid={`hero-social-${k.toLowerCase()}`} className="text-xs hover:text-white capitalize">{k}</a>)}
                {contact.public_email && <a href={`mailto:${contact.public_email}`} className="text-xs hover:text-white">{contact.public_email}</a>}
                {contact.public_phone && <span className="text-xs">{contact.public_phone}</span>}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-row sm:flex-col gap-2 shrink-0">
            <Link to={`/live/teams/${team.slug}/portfolio`} data-testid="team-portfolio-link" className="flex items-center justify-center gap-2 px-4 py-2 rounded-sm text-sm font-semibold uppercase tracking-wider bg-white/10 text-white hover:bg-white/20 transition-colors focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none"><FileText className="w-4 h-4" /> Portfolio</Link>
            <button data-testid="follow-team-btn" onClick={toggleFollow} disabled={followBusy} className={`flex items-center justify-center gap-2 px-4 py-2 rounded-sm text-sm font-semibold uppercase tracking-wider transition-colors disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none ${following ? "bg-white text-[#0A192F]" : "bg-[#E63946] text-white hover:bg-[#c62f3b]"}`}><Heart className={`w-4 h-4 ${following ? "fill-[#E63946] text-[#E63946]" : ""}`} /> {following ? "Following" : "Follow"}{team.follow_count > 0 && <span className="text-xs opacity-70">· {team.follow_count}</span>}</button>
            {canManage && <button data-testid="edit-team-photo-btn" onClick={() => setEditPhoto((v) => !v)} className="flex items-center justify-center gap-2 px-4 py-2 rounded-sm text-xs font-semibold uppercase tracking-wider bg-white/10 text-white/80 hover:bg-white/20 transition-colors"><Camera className="w-3.5 h-3.5" /> {photo ? "Change Photo" : "Add Photo"}</button>}
          </div>
        </div>

        {canManage && editPhoto && (
          <div className="relative mt-4 bg-white text-slate-800 rounded-sm p-4 max-w-md" data-testid="team-photo-editor">
            <ImageUploadField label="Primary team photo (used in hero & media)" testid="team-photo-upload" value={photo ? normalizeImageUrl(photo) : ""} onChange={savePhoto} />
            {photo && <button onClick={() => savePhoto("")} className="mt-2 text-xs text-[#E63946] font-semibold">Remove photo</button>}
          </div>
        )}

        {!canManage && team.management_status === "cbr_managed" && (
          <Link to={`/live/claim?type=team&id=${team.id}&name=${encodeURIComponent(team.name)}`} data-testid="claim-team-cta" className="inline-flex items-center gap-1.5 mt-4 text-[11px] font-semibold uppercase tracking-wider text-white/70 hover:text-white border border-white/20 rounded-full px-3 py-1"><BadgeCheck className="w-3.5 h-3.5" /> Claim this team</Link>
        )}
        <DataQuality dq={team.data_quality} />
      </div>
    </div>
  );
}

const HeroStat = ({ label, value, testid }) => (
  <div data-testid={testid} className="bg-white/10 rounded-sm px-3 py-2 min-w-[74px]">
    <div className="text-[9px] font-semibold uppercase tracking-wider text-white/50">{label}</div>
    <div className="font-data text-xl leading-tight">{value}</div>
  </div>
);

/* ------------------------------------------------------------------ Overview */
function OverviewTab({ team, rt, upcoming, results, canInviteStaff, setTab }) {
  const next = upcoming[0];
  const recent = results.results?.[0];
  const roster = team.roster || [];
  const accomplishments = team.accomplishments || [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-6" data-testid="team-overview-tab">
      <div className="md:col-span-12"><ReportCard teamId={team.id} /></div>

      <div className="md:col-span-4"><NextGameCard g={next} onView={() => setTab("schedule")} /></div>
      <div className="md:col-span-4"><RecentResultCard g={recent} onView={() => setTab("results")} /></div>
      <div className="md:col-span-4"><RecordCard record={team.record} summary={results.summary} /></div>

      <div className="md:col-span-7">
        <PreviewCard icon={Users} title={`Roster (${roster.length})`} onView={roster.length ? () => setTab("roster") : null}>
          {roster.length ? (
            <div className="grid sm:grid-cols-2 gap-2" data-testid="overview-roster-preview">
              {roster.slice(0, 6).map((p) => (
                <Link key={p.player_id} to={`/prospects/${p.slug || p.player_id}`} className="flex items-center gap-3 p-2 border border-slate-100 rounded-sm hover:border-[#E63946] transition-colors">
                  <div className="w-8 h-8 rounded-sm bg-slate-100 overflow-hidden grid place-items-center shrink-0">{p.profile_photo_url ? <img src={normalizeImageUrl(p.profile_photo_url)} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-slate-400">{p.first_name?.[0]}{p.last_name?.[0]}</span>}</div>
                  <div className="min-w-0"><div className="text-sm font-semibold truncate">{p.number ? `#${p.number} ` : ""}{p.first_name} {p.last_name}</div><div className="text-[11px] text-slate-400 truncate">{[p.primary_position, p.graduation_year].filter(Boolean).join(" · ")}</div></div>
                </Link>
              ))}
            </div>
          ) : <Empty>No players on the roster yet.</Empty>}
        </PreviewCard>
      </div>

      <div className="md:col-span-5"><StaffCard teamId={team.id} canInvite={canInviteStaff} /></div>

      <div className="md:col-span-6">
        <PreviewCard icon={BarChart3} title="Key Statistics" onView={() => setTab("stats")}>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {[["Team AVG", team.stats?.team_avg], ["OPS", team.stats?.ops], ["ERA", team.stats?.era], ["WHIP", team.stats?.whip], ["Runs", team.stats?.runs_scored ?? 0], ["HR", team.stats?.hr ?? 0]].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-slate-100 pb-1"><span className="text-slate-500">{k}</span><span className="font-data text-[#0A192F]">{v == null || v === "" ? "—" : v}</span></div>
            ))}
          </div>
        </PreviewCard>
      </div>

      {accomplishments.length > 0 && (
        <div className="md:col-span-6">
          <PreviewCard icon={Award} title="Accomplishments">
            <div className="space-y-2" data-testid="team-accomplishments">
              {accomplishments.slice(0, 5).map((a, i) => (
                <div key={i} className="flex items-center gap-3 p-2 border border-slate-100 rounded-sm">
                  <Trophy className="w-4 h-4 text-[#E63946] shrink-0" />
                  <div className="min-w-0"><div className="text-sm font-semibold truncate">{a.title}</div><div className="text-[11px] text-slate-400">{[a.season, a.placement].filter(Boolean).join(" · ")}</div></div>
                </div>
              ))}
            </div>
          </PreviewCard>
        </div>
      )}
    </div>
  );
}

function PreviewCard({ icon: Icon, title, onView, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 md:p-5 h-full">
      <div className="flex items-center justify-between mb-3">
        <div className="label-overline flex items-center gap-2"><Icon className="w-4 h-4 text-[#E63946]" />{title}</div>
        {onView && <button onClick={onView} className="text-[11px] font-semibold uppercase tracking-wider text-[#0A192F] hover:text-[#E63946] inline-flex items-center gap-0.5">View all <ChevronRight className="w-3.5 h-3.5" /></button>}
      </div>
      {children}
    </div>
  );
}

function NextGameCard({ g, onView }) {
  if (!g) return <div className="bg-white border border-slate-200 rounded-sm p-4 md:p-5 h-full" data-testid="overview-next-game"><div className="label-overline flex items-center gap-2 mb-3"><Calendar className="w-4 h-4 text-[#E63946]" /> Next Game</div><Empty>No upcoming games.</Empty></div>;
  const v = g._view || {}; const opp = v.opponent || {}; const loc = v.location || "home";
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 md:p-5 h-full flex flex-col" data-testid="overview-next-game">
      <div className="flex items-center justify-between mb-3"><div className="label-overline flex items-center gap-2"><Calendar className="w-4 h-4 text-[#E63946]" /> Next Game</div>{g.status === "live" && <span className="text-[10px] font-bold text-white bg-[#E63946] px-1.5 py-0.5 rounded-sm animate-pulse">LIVE</span>}</div>
      <div className="text-sm font-semibold text-[#0A192F]"><span className="text-slate-400">{loc === "away" ? "@" : "vs"}</span> {opp.name}</div>
      <div className="text-[11px] text-slate-400 mt-1">{[g.date, g.time, g.location].filter(Boolean).join(" · ")}</div>
      <div className="text-[11px] text-slate-400 capitalize">{(g.game_type || "").replace(/_/g, " ")}</div>
      <button onClick={onView} className="mt-auto pt-3 text-[11px] font-semibold uppercase tracking-wider text-[#0A192F] hover:text-[#E63946] inline-flex items-center gap-0.5 self-start">Full schedule <ChevronRight className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function RecentResultCard({ g, onView }) {
  if (!g) return <div className="bg-white border border-slate-200 rounded-sm p-4 md:p-5 h-full" data-testid="overview-recent-result"><div className="label-overline flex items-center gap-2 mb-3"><ClipboardCheck className="w-4 h-4 text-[#E63946]" /> Recent Result</div><Empty>No results yet.</Empty></div>;
  const v = g._view || {}; const opp = v.opponent || {}; const loc = v.location || "home";
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 md:p-5 h-full flex flex-col" data-testid="overview-recent-result">
      <div className="label-overline flex items-center gap-2 mb-3"><ClipboardCheck className="w-4 h-4 text-[#E63946]" /> Recent Result</div>
      <div className="flex items-center gap-2 text-sm font-semibold text-[#0A192F] flex-wrap"><span className="text-slate-400">{loc === "away" ? "@" : "vs"}</span> {opp.name} <span className="font-data">{v.us}-{v.them}</span> <ResultBadge outcome={v.outcome} /></div>
      <div className="text-[11px] text-slate-400 mt-1">{[g.date, g.location].filter(Boolean).join(" · ")}</div>
      <button onClick={onView} className="mt-auto pt-3 text-[11px] font-semibold uppercase tracking-wider text-[#0A192F] hover:text-[#E63946] inline-flex items-center gap-0.5 self-start">All results <ChevronRight className="w-3.5 h-3.5" /></button>
    </div>
  );
}

/* ------------------------------------------------------------------ Schedule */
function ScheduleTab({ team, sched, upcoming, canManage, showForm, setShowForm, onChange }) {
  const groups = groupByDate(upcoming);
  return (
    <div data-testid="team-schedule-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="label-overline flex items-center gap-2"><Calendar className="w-4 h-4 text-[#E63946]" /> Upcoming Schedule</div>
        {canManage && <button data-testid="schedule-game-btn" onClick={() => setShowForm(!showForm)} className="text-xs font-semibold uppercase tracking-wider bg-[#0A192F] text-white px-3 py-1.5 rounded-sm flex items-center gap-1">{showForm ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}{showForm ? "Cancel" : "Schedule Game"}</button>}
      </div>
      {showForm && <GameForm teamId={team.id} onDone={() => { setShowForm(false); onChange(); }} />}
      {upcoming.length === 0 ? <Empty>No upcoming games scheduled.</Empty> : (
        <div className="space-y-5">
          {groups.map(([label, games]) => (
            <div key={label}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">{label}</div>
              <div className="bg-white border border-slate-200 rounded-sm px-4">
                {games.map((g) => <GameRow key={g.id} g={g} live={g.status === "live"} canManage={canManage} onChange={onChange} />)}
              </div>
            </div>
          ))}
        </div>
      )}
      {canManage && sched.archived?.length > 0 && (
        <div className="mt-6">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Archived (excluded from record & rankings)</div>
          <div className="bg-white border border-dashed border-slate-200 rounded-sm px-4">{sched.archived.map((g) => <GameRow key={g.id} g={g} past canManage={canManage} onChange={onChange} />)}</div>
        </div>
      )}
    </div>
  );
}

function groupByDate(games) {
  const map = {};
  games.forEach((g) => {
    const key = g.tournament?.name || (g.date ? monthLabel(g.date) : "TBD");
    (map[key] = map[key] || []).push(g);
  });
  return Object.entries(map);
}
function monthLabel(d) { try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "long", year: "numeric" }); } catch { return d; } }

/* ------------------------------------------------------------------ Results */
function ResultsTab({ results }) {
  const s = results.summary; const rows = results.results || [];
  return (
    <div data-testid="team-results-tab">
      {s && s.games > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <HeroSummary label="Record" value={`${s.w}-${s.l}${s.t ? `-${s.t}` : ""}`} testid="results-record" />
          <HeroSummary label="Runs Scored" value={s.runs_scored} testid="results-rs" />
          <HeroSummary label="Runs Allowed" value={s.runs_allowed} testid="results-ra" />
          <HeroSummary label="Run Diff" value={`${s.run_differential > 0 ? "+" : ""}${s.run_differential}`} testid="results-diff" tone={s.run_differential > 0 ? "pos" : s.run_differential < 0 ? "neg" : ""} />
        </div>
      )}
      {rows.length === 0 ? <Empty>No completed results yet.</Empty> : (
        <div className="bg-white border border-slate-200 rounded-sm px-4">
          {rows.map((g) => <GameRow key={g.id} g={g} past canManage={false} onChange={() => {}} />)}
        </div>
      )}
    </div>
  );
}
function HeroSummary({ label, value, testid, tone }) {
  const t = tone === "pos" ? "text-emerald-600" : tone === "neg" ? "text-[#E63946]" : "text-[#0A192F]";
  return <div data-testid={testid} className="bg-white border border-slate-200 rounded-sm p-4"><div className="label-overline text-[10px]">{label}</div><div className={`font-data text-2xl mt-1 ${t}`}>{value}</div></div>;
}

/* ------------------------------------------------------------------ shared bits (preserved) */
const GRADE_COLOR = (g) => {
  if (!g) return "bg-slate-100 text-slate-400";
  const l = g[0];
  return l === "A" ? "bg-emerald-500 text-white" : l === "B" ? "bg-sky-500 text-white" : l === "C" ? "bg-amber-500 text-white" : l === "D" ? "bg-orange-500 text-white" : "bg-[#E63946] text-white";
};
const MOMENTUM_ICON = { hot: Flame, warm: TrendingUp, even: Minus, cool: TrendingDown, cold: TrendingDown, none: Minus };
const CONF_COLOR = { very_high: "bg-emerald-100 text-emerald-700", high: "bg-emerald-100 text-emerald-700", medium: "bg-amber-100 text-amber-700", low: "bg-slate-100 text-slate-500", limited: "bg-slate-100 text-slate-500" };

function GradeTile({ label, grade }) {
  return (
    <div data-testid={`report-grade-${label.toLowerCase().replace(/[^a-z]/g, "-")}`} className="flex flex-col items-center">
      <div className={`w-full aspect-square max-h-24 rounded-sm grid place-items-center font-display text-3xl sm:text-4xl tracking-tighter ${GRADE_COLOR(grade)}`}>{grade || "—"}</div>
      <div className="label-overline text-[10px] mt-2 text-center leading-tight">{label}</div>
    </div>
  );
}

function ReportCard({ teamId }) {
  const [card, setCard] = useState(null);
  useEffect(() => { api.get(`/live/teams/${teamId}/report-card`).then(({ data }) => setCard(data)).catch(() => setCard(false)); }, [teamId]);
  if (!card) return null;
  const o = card.overall || {}; const g = card.grades || {};
  const MIcon = MOMENTUM_ICON[card.momentum?.code] || Minus;
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm" data-testid="team-report-card">
      <div className="bg-[#0A192F] text-white px-5 py-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-[#E63946]" /><span className="font-display text-lg uppercase tracking-tight">CBR Team Report Card™</span>{o.overall_grade && <span className={`ml-auto font-display text-xl px-2.5 py-0.5 rounded-sm ${GRADE_COLOR(o.overall_grade)}`} data-testid="report-overall-grade">{o.overall_grade}</span>}</div>
      <div className="p-5">
        <div className="grid grid-cols-3 gap-4 pb-4 border-b border-slate-100">
          <div><div className="label-overline text-[10px]">CBR Rating</div><div className="font-data text-3xl text-[#0A192F]" data-testid="report-rating">{o.cbr_team_rating != null ? Number(o.cbr_team_rating).toFixed(2) : "—"}</div></div>
          <div><div className="label-overline text-[10px]">National Rank</div><div className="font-data text-3xl text-[#0A192F]">{rk(o.national_rank)}</div></div>
          <div><div className="label-overline text-[10px]">Provincial Rank</div><div className="font-data text-3xl text-[#0A192F]">{rk(o.provincial_rank)}</div></div>
        </div>
        <div className="grid grid-cols-4 gap-3 py-4">
          <GradeTile label="Offense" grade={g.offense?.grade} />
          <GradeTile label="Defense" grade={g.defense?.grade} />
          <GradeTile label="Pitching" grade={g.pitching?.grade} />
          <GradeTile label="Strength of Schedule" grade={g.strength_of_schedule?.grade} />
        </div>
        <div className="flex items-center justify-between gap-4 pt-3 border-t border-slate-100 flex-wrap">
          <div className="flex items-center gap-2" data-testid="report-momentum"><span className="label-overline text-[10px]">Momentum</span><span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A192F]"><MIcon className={`w-4 h-4 ${card.momentum?.code === "hot" ? "text-[#E63946]" : card.momentum?.code?.startsWith("co") ? "text-slate-400" : "text-emerald-600"}`} /> {card.momentum?.label}</span></div>
          <div className="flex items-center gap-2" data-testid="report-confidence"><span className="label-overline text-[10px]">Confidence</span><span className={`text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm ${CONF_COLOR[card.confidence?.level] || CONF_COLOR.limited}`}>{card.confidence?.label}</span></div>
          <Link to="/live/team-rankings/how-it-works" data-testid="how-rankings-link" className="flex items-center gap-1 text-xs font-semibold text-[#0A192F] hover:text-[#E63946]"><Info className="w-3.5 h-3.5" /> How Rankings Work</Link>
        </div>
        {!card.has_enough_data && <p className="text-[11px] text-slate-400 mt-3">Grades sharpen as this team plays more games.</p>}
      </div>
    </div>
  );
}

const GAME_TYPES = [["exhibition", "Exhibition / Scrimmage"], ["league", "League Game"], ["tournament", "Tournament"], ["tournament_playoff", "Tournament Playoff"], ["provincial_championship", "Provincial Championship"], ["national_championship", "National Championship"], ["international", "International"]];
const TOURNAMENT_TYPES = ["tournament", "tournament_playoff", "provincial_championship", "national_championship", "international"];

function GameForm({ teamId, onDone }) {
  const [f, setF] = useState({ opponent_name: "", date: "", time: "", location: "", game_type: "league", home_away: "home", league_id: "", playoff_round: "final", tournament: { name: "", location: "", organizer: "", tier: "" } });
  const [leagues, setLeagues] = useState([]);
  useEffect(() => { api.get("/live/leagues", { params: { active: true } }).then(({ data }) => setLeagues(data)).catch(() => {}); }, []);
  const isTourn = TOURNAMENT_TYPES.includes(f.game_type);
  const setT = (k, v) => setF({ ...f, tournament: { ...f.tournament, [k]: v } });
  const submit = () => {
    if (!f.opponent_name || !f.date) return toast.error("Opponent and date required");
    if (f.game_type === "league" && !f.league_id) return toast.error("Select a league for league games");
    if (isTourn && !f.tournament.name) return toast.error("Tournament name is required");
    const body = { opponent_name: f.opponent_name, date: f.date, time: f.time, location: f.location, game_type: f.game_type, home_away: f.home_away };
    if (f.game_type === "league") body.league_id = f.league_id;
    if (isTourn) body.tournament = f.tournament;
    if (f.game_type === "tournament_playoff") body.playoff_round = f.playoff_round;
    api.post(`/live/teams/${teamId}/games`, body).then(() => { toast.success("Game scheduled"); onDone(); }).catch((e) => toast.error(e?.response?.data?.detail || "Failed to schedule"));
  };
  const inp = "border border-slate-200 rounded-sm px-2 py-1.5 text-sm";
  return (
    <div className="border border-slate-200 rounded-sm p-4 mb-4 grid sm:grid-cols-2 gap-2 bg-white" data-testid="game-form">
      <input data-testid="game-opponent" placeholder="Opponent" value={f.opponent_name} onChange={(e) => setF({ ...f, opponent_name: e.target.value })} className={inp} />
      <input data-testid="game-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inp} />
      <input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} className={inp} />
      <input placeholder="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className={inp} />
      <select data-testid="game-type" value={f.game_type} onChange={(e) => { const gt = e.target.value; setF({ ...f, game_type: gt, league_id: gt === "league" ? f.league_id : "", tournament: TOURNAMENT_TYPES.includes(gt) ? f.tournament : { name: "", location: "", organizer: "", tier: "" } }); }} className={inp}>{GAME_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      <select value={f.home_away} onChange={(e) => setF({ ...f, home_away: e.target.value })} className={inp}>{["home", "away", "neutral"].map((t) => <option key={t} value={t}>{t}</option>)}</select>
      {f.game_type === "league" && <select data-testid="game-league" value={f.league_id} onChange={(e) => setF({ ...f, league_id: e.target.value })} className={`${inp} sm:col-span-2`}><option value="">Select league…</option>{leagues.map((l) => <option key={l.id} value={l.id}>{l.abbreviation ? `${l.abbreviation} — ` : ""}{l.name}</option>)}</select>}
      {isTourn && <><input data-testid="game-tournament-name" placeholder="Tournament name" value={f.tournament.name} onChange={(e) => setT("name", e.target.value)} className={`${inp} sm:col-span-2`} /><input placeholder="Tournament location" value={f.tournament.location} onChange={(e) => setT("location", e.target.value)} className={inp} /><input placeholder="Organizer" value={f.tournament.organizer} onChange={(e) => setT("organizer", e.target.value)} className={inp} /></>}
      {f.game_type === "tournament_playoff" && <select data-testid="game-playoff-round" value={f.playoff_round} onChange={(e) => setF({ ...f, playoff_round: e.target.value })} className={`${inp} sm:col-span-2`}>{["quarterfinal", "semifinal", "final"].map((r) => <option key={r} value={r}>{r}</option>)}</select>}
      <button data-testid="game-submit" onClick={submit} className="sm:col-span-2 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider py-2 rounded-sm">Add to Schedule</button>
    </div>
  );
}

const LOC_LABEL = { home: "Home", away: "Away", neutral: "Neutral" };
function ResultBadge({ outcome }) {
  if (!outcome) return null;
  const cls = outcome === "W" ? "bg-emerald-100 text-emerald-700" : outcome === "L" ? "bg-red-100 text-[#E63946]" : "bg-slate-100 text-slate-500";
  return <span data-testid="result-badge" className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${cls}`}>{outcome}</span>;
}
function RatingChange({ change }) {
  if (change == null) return null;
  const up = change > 0;
  return <span data-testid="rating-change" className={`inline-flex items-center gap-0.5 font-data ${up ? "text-emerald-600" : change < 0 ? "text-[#E63946]" : "text-slate-400"}`}>{up ? "+" : ""}{change.toFixed(2)}</span>;
}

function GameActions({ g, onChange }) {
  const [open, setOpen] = useState(false);
  const st = g.status;
  const run = (p, ok) => { p.then(() => { toast.success(ok); onChange && onChange(); }).catch((e) => toast.error(e?.response?.data?.detail || "Failed")); setOpen(false); };
  const del = () => { if (window.confirm("Delete this game permanently? This cannot be undone.")) run(api.delete(`/live/games/${g.id}`), "Game deleted"); };
  const item = "w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm";
  return (
    <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
      <button data-testid={`game-actions-${g.id}`} onClick={() => setOpen(!open)} className="p-1.5 text-slate-400 hover:text-[#0A192F]"><MoreVertical className="w-4 h-4" /></button>
      {open && (<>
        <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
        <div className="absolute right-0 top-8 z-20 w-48 bg-white border border-slate-200 rounded-sm shadow-lg py-1" data-testid={`game-menu-${g.id}`}>
          <Link to={`/live/games/${g.id}`} className={item} data-testid={`game-view-${g.id}`}><Eye className="w-3.5 h-3.5" /> View</Link>
          {["draft", "scheduled", "pregame", "lineups_locked", "live"].includes(st) && <Link to={`/live/games/${g.id}`} className={item} data-testid={`game-continue-${g.id}`}><Play className="w-3.5 h-3.5" /> {st === "live" ? "Continue Scoring" : "Open / Edit"}</Link>}
          <button className={item} data-testid={`game-duplicate-${g.id}`} onClick={() => run(api.post(`/live/games/${g.id}/duplicate`), "Draft created from this game")}><Copy className="w-3.5 h-3.5" /> Duplicate</button>
          {st === "final" && <button className={item} data-testid={`game-archive-${g.id}`} onClick={() => run(api.post(`/live/games/${g.id}/archive`), "Game archived")}><ArchiveIcon className="w-3.5 h-3.5" /> Archive</button>}
          {st === "archived" && <button className={item} data-testid={`game-restore-${g.id}`} onClick={() => run(api.post(`/live/games/${g.id}/restore`), "Game restored")}><RotateCcw className="w-3.5 h-3.5" /> Restore</button>}
          <button className={`${item} text-[#E63946]`} data-testid={`game-delete-${g.id}`} onClick={del}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
        </div>
      </>)}
    </div>
  );
}

function GameRow({ g, live, past, canManage, onChange }) {
  const v = g._view || {}; const opp = v.opponent || {};
  const loc = v.location || (g.home_away === "away" ? "away" : "home");
  const isFinal = g.status === "final"; const isArchived = g.status === "archived";
  return (
    <div data-testid={`game-row-${g.id}`} className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0 group">
      <div className="w-14 text-center shrink-0">{live ? <span className="text-[10px] font-bold text-white bg-[#E63946] px-1.5 py-0.5 rounded-sm animate-pulse">LIVE</span> : isArchived ? <span className="text-[9px] font-bold text-slate-400 uppercase">Arch</span> : <span className="text-[11px] text-slate-400">{(g.date || "").slice(5)}</span>}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-semibold flex-wrap">
          <span className="text-slate-400">{loc === "away" ? "@" : "vs"}</span>
          {opp.slug ? <Link to={`/live/teams/${opp.slug}`} data-testid={`game-opp-link-${g.id}`} className="hover:text-[#E63946] truncate">{opp.name}</Link> : <span className="truncate">{opp.name}</span>}
          {isFinal && <span className="font-data text-slate-600">{v.us}-{v.them}</span>}
          {isFinal && <ResultBadge outcome={v.outcome} />}
          {v.quality_win && <span data-testid={`quality-win-${g.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold bg-[#E63946]/10 text-[#E63946] px-1.5 py-0.5 rounded-sm"><Trophy className="w-3 h-3" /> Quality Win</span>}
        </div>
        <div className="text-[11px] text-slate-400 flex items-center gap-1.5 flex-wrap">
          <span className="capitalize">{(g.game_type || "").replace(/_/g, " ")}</span>
          <span className="text-slate-300">·</span><span>{LOC_LABEL[loc]}</span>
          {g.tournament?.name && <><span className="text-slate-300">·</span><span>{g.tournament.name}</span></>}
          {g.location && <><span className="text-slate-300">·</span><span>{g.location}</span></>}
          {isFinal && v.rating_before != null && <><span className="text-slate-300">·</span><span className="font-data text-slate-500">{Number(v.rating_before).toFixed(2)} → {Number(v.rating_after).toFixed(2)}</span><RatingChange change={v.rating_change} /></>}
        </div>
      </div>
      {canManage ? <GameActions g={g} onChange={onChange} /> : <Link to={`/live/games/${g.id}`} data-testid={`game-link-${g.id}`} className="shrink-0"><ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#E63946]" /></Link>}
    </div>
  );
}

function RecordCard({ record, summary }) {
  const useSum = summary && summary.games > 0 && summary.record;
  const src = useSum ? summary.record : record;
  const o = src?.overall || { w: 0, l: 0, t: 0 };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 md:p-5 h-full" data-testid="team-record">
      <div className="label-overline text-[#E63946]">Team Record</div>
      <div className="font-data text-4xl text-[#0A192F] mt-2">{o.w}-{o.l}{o.t ? `-${o.t}` : ""}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500 mt-3">
        {["league", "tournament", "home", "away"].map((k) => { const r = src?.[k] || { w: 0, l: 0 }; return <div key={k} className="flex justify-between"><span className="capitalize">{k}</span><span className="font-data text-slate-700">{r.w}-{r.l}</span></div>; })}
      </div>
    </div>
  );
}

function StaffCard({ teamId, canInvite }) {
  const ROLE_LABEL = { org_owner: "Org Owner", org_admin: "Org Admin", head_coach: "Head Coach", assistant_coach: "Assistant Coach", pitching_coach: "Pitching Coach", hitting_coach: "Hitting Coach", team_manager: "Team Manager", trainer: "Trainer", scorekeeper: "Scorekeeper" };
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("head_coach");
  const [link, setLink] = useState("");
  const load = () => api.get(`/live/teams/${teamId}/members`).then(({ data }) => setStaff(data)).catch(() => setStaff([]));
  useEffect(() => { load(); }, [teamId]);
  const invite = () => { if (!email) return toast.error("Enter an email"); api.post(`/live/teams/${teamId}/members/invite`, { scope_type: "team", role, email }).then(({ data }) => { setLink(data.invite_link); setEmail(""); toast.success("Invite created"); }).catch((e) => toast.error(e?.response?.data?.detail || "Failed")); };
  const remove = (gid) => api.delete(`/live/teams/${teamId}/members/${gid}`).then(() => { toast.success("Removed"); load(); }).catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 md:p-5 h-full" data-testid="team-staff-card">
      <div className="flex items-center justify-between"><div className="label-overline flex items-center gap-2"><Users2 className="w-4 h-4 text-[#E63946]" /> Team Staff</div>{canInvite && <button data-testid="invite-staff-btn" onClick={() => setOpen(!open)} className="text-xs font-semibold text-[#E63946] flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" /> Invite</button>}</div>
      {open && canInvite && (
        <div className="mt-3 p-3 bg-slate-50 rounded-sm" data-testid="invite-staff-form">
          <div className="flex gap-2 flex-wrap">
            <input data-testid="invite-staff-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="coach@email.com" className="flex-1 min-w-[140px] border border-slate-200 rounded-sm px-2 py-1.5 text-sm" />
            <select data-testid="invite-staff-role" value={role} onChange={(e) => setRole(e.target.value)} className="border border-slate-200 rounded-sm px-2 py-1.5 text-sm">{["head_coach", "assistant_coach", "pitching_coach", "hitting_coach", "team_manager", "trainer", "scorekeeper"].map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}</select>
            <button data-testid="invite-staff-submit" onClick={invite} className="bg-[#0A192F] text-white text-xs font-semibold uppercase tracking-wider px-3 rounded-sm">Send</button>
          </div>
          {link && <div className="mt-2 flex items-center gap-2 bg-white border border-slate-200 rounded-sm p-2"><span className="text-[11px] truncate flex-1">{link}</span><button data-testid="copy-staff-invite" onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }} className="text-[11px] font-semibold flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button></div>}
        </div>
      )}
      <ul className="mt-3 space-y-2" data-testid="team-staff-list">
        {staff.length === 0 ? <li className="text-sm text-slate-400">No staff assigned yet.</li> : staff.map((m, i) => (
          <li key={i} className="flex items-center gap-2 text-sm" data-testid={`staff-row-${i}`}>
            <span className="w-8 h-8 rounded-sm bg-slate-100 overflow-hidden grid place-items-center shrink-0">{m.photo_url ? <img src={normalizeImageUrl(m.photo_url)} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-slate-400">{(m.name || "?")[0]}</span>}</span>
            <div className="min-w-0 flex-1"><div className="font-semibold truncate">{m.name || "Unnamed"}</div>{m.bio && <div className="text-[11px] text-slate-400 truncate">{m.bio}</div>}</div>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 shrink-0">{ROLE_LABEL[m.role] || m.role}</span>
            {canInvite && m.grant_id && <button data-testid={`remove-staff-${i}`} onClick={() => remove(m.grant_id)} className="text-slate-300 hover:text-[#E63946] shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>}
          </li>
        ))}
      </ul>
    </div>
  );
}

const DQ_META = {
  verified_live: { label: "Verified Live Data", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  mixed: { label: "Mixed Data", cls: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  manual: { label: "Manual Results Only", cls: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
};
function DataQuality({ dq }) {
  const m = DQ_META[dq];
  if (!m) return null;
  return <span data-testid="team-data-quality" className={`inline-flex items-center gap-1.5 mt-3 ml-2 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border ${m.cls}`}><span className="w-1.5 h-1.5 rounded-full bg-current" /> {m.label}</span>;
}

const Shell = ({ children }) => <div className="min-h-screen bg-[#F1F3F6]"><Header />{children}</div>;
const Empty = ({ children }) => <div className="py-8 text-center text-sm text-slate-400">{children}</div>;
